import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, BadgeCheck, Mail, Phone, Route as RouteIcon, User } from "lucide-react";
import type { ReactNode } from "react";
import { PageBody } from "@/components/crm/AppShell";
import { ContactActions, CopyButton } from "@/components/crm/ContactActions";
import {
  LeadControls,
  LeadSourceBadge,
  LeadStatusBadge,
  LeadTemperatureBadge,
  type LeadControlsData,
} from "@/components/crm/LeadControls";
import { CustomFieldsForm, type FieldValue } from "@/components/crm/CustomFieldsForm";
import { LeadTags, type LeadTag } from "@/components/crm/LeadTags";
import { TaskList, type Task } from "@/components/crm/TaskList";
import {
  LeadTimeline,
  type TimelineActivity,
  type TimelineNote,
} from "@/components/crm/LeadTimeline";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { formatDateTime, formatPhone, relativeTime, sourceLabel } from "@/lib/crm";
import { cn } from "@/lib/utils";

type Attribution = {
  platform: string | null;
  source: string | null;
  campaignName: string | null;
  campaignExternalId: string | null;
  adsetName: string | null;
  adName: string | null;
  adExternalId: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  fbclid: string | null;
  fbc: string | null;
  fbp: string | null;
  gclid: string | null;
  ctwaClid: string | null;
  landingPage: string | null;
  referrer: string | null;
  occurredAt: string;
};

type Touchpoint = {
  id: string;
  position: number;
  channel: string | null;
  campaignName: string | null;
  source: string | null;
  occurredAt: string;
};

type LeadDetail = LeadControlsData & {
  leadScore: number;
  source: string | null;
  createdAt: string;
  firstResponseAt: string | null;
  businessResponseTimeSeconds: number | null;
  rawPayload: Record<string, unknown>;
  contact: {
    id: string;
    fullName: string | null;
    phone: string | null;
    phoneNormalized: string | null;
    email: string | null;
    isCustomer: boolean;
    consentAt: string | null;
  };
  attribution: Attribution | null;
  firstTouch: Attribution | null;
  journey: Touchpoint[];
  timeline: TimelineActivity[];
  notes: TimelineNote[];
  tags: LeadTag[];
  customFields: FieldValue[];
};

export const Route = createFileRoute("/_authenticated/leads/$leadId")({
  component: LeadDetailPage,
});

/**
 * Ficha do lead — UMA captação de uma pessoa.
 *
 * Três andares: o cabeçalho diz quem é, em que pé está e o que dá para fazer
 * (falar, mudar status e responsável — os únicos controles editáveis); a
 * coluna principal é a linha do tempo, onde o atendimento acontece; a lateral
 * guarda o que se consulta de vez em quando (etiquetas, campos, atribuição).
 *
 * No celular a lateral vai para baixo da linha do tempo: quem abre a ficha
 * no corredor quer registrar a ligação que acabou de fazer, não conferir o
 * utm_campaign.
 */
function LeadDetailPage() {
  const { leadId } = Route.useParams();
  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", leadId],
    queryFn: () => api.get<LeadDetail>(`/leads/${leadId}`),
  });

  // Tarefas em consulta própria, e não dentro da ficha: elas mudam sozinhas
  // enquanto a ficha está aberta — alguém conclui pela tela de Tarefas — e
  // recarregar a ficha inteira por causa disso piscaria a página toda.
  const tasksKey = ["tasks", "lead", leadId];
  const tasks = useQuery({
    queryKey: tasksKey,
    queryFn: () => api.get<{ data: Task[] }>("/tasks", { leadId, scope: "all", pageSize: 50 }),
  });

  if (isLoading) return <FichaCarregando />;
  if (!lead) return null;

  const nome = lead.contact.fullName ?? "Sem nome";
  const telefone = lead.contact.phoneNormalized ?? lead.contact.phone;
  const pendentes = (tasks.data?.data ?? []).filter((t) => t.status === "pending").length;
  const answers = respostasDoFormulario(lead.rawPayload);

  return (
    <PageBody className="space-y-5">
      <Link
        to="/leads"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Leads
      </Link>

      {/* ── Cabeçalho: quem é, em que pé está, o que dá para fazer ─────────── */}
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-5 p-5 sm:p-6 md:flex-row md:items-start">
          <Avatar name={nome} seed={telefone ?? lead.id} size="xl" className="shrink-0" />

          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <h1 className="font-display text-xl font-semibold leading-tight tracking-tight sm:text-2xl">
                {nome}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <LeadStatusBadge status={lead.status} />
                <LeadTemperatureBadge temperature={lead.temperature} />
                {lead.source && <LeadSourceBadge source={lead.source} />}
                {lead.contact.isCustomer && (
                  <Badge variant="success">
                    <BadgeCheck />
                    Cliente
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
              {telefone && (
                <span className="inline-flex items-center gap-1 tabular-nums">
                  <Phone className="size-3.5 text-muted-foreground" />
                  {formatPhone(telefone)}
                  <CopyButton value={telefone} label="telefone" />
                </span>
              )}
              {lead.contact.email && (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <Mail className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{lead.contact.email}</span>
                  <CopyButton value={lead.contact.email} label="e-mail" />
                </span>
              )}
              {/* Ponte entre as duas visões: esta é UMA captação; lá está a
                  história inteira da pessoa. */}
              <Link
                to="/contatos/$contactId"
                params={{ contactId: lead.contact.id }}
                className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
              >
                <User className="size-3.5" />
                Ver contato
              </Link>
            </div>
          </div>

          <div className="md:shrink-0">
            <ContactActions leadId={lead.id} phone={telefone} />
          </div>
        </div>

        {/* Faixa de atendimento: os únicos controles editáveis da ficha,
            visíveis sem rolar. */}
        <div className="border-t bg-muted/30 px-5 py-4 sm:px-6">
          <LeadControls lead={lead} />
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-6">
        {/* ── Linha do tempo: onde o atendimento acontece ──────────────────── */}
        <section className="min-w-0">
          <Card>
            <Tabs defaultValue="historico">
              <div className="px-5 pt-3">
                {/* Margem negativa: o sublinhado das abas corre de borda a
                    borda do cartão, não só sob o texto. */}
                <TabsList className="-mx-5 px-5">
                  <TabsTrigger value="historico">Linha do tempo</TabsTrigger>
                  <TabsTrigger value="tarefas">
                    Tarefas
                    {pendentes > 0 && (
                      <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[11px] font-semibold tabular-nums text-primary">
                        {pendentes}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="historico" className="px-5 pb-5 pt-5">
                <LeadTimeline leadId={lead.id} activities={lead.timeline} notes={lead.notes} />
              </TabsContent>
              <TabsContent value="tarefas" className="px-5 pb-5 pt-5">
                <TaskList
                  tasks={tasks.data?.data ?? []}
                  loading={tasks.isLoading}
                  leadId={leadId}
                  queryKey={tasksKey}
                />
              </TabsContent>
            </Tabs>
          </Card>
        </section>

        {/* ── Lateral: o que se consulta, não o que se muda ────────────────── */}
        <aside className="min-w-0 space-y-5">
          <Bloco title="Etiquetas">
            <LeadTags leadId={lead.id} tags={lead.tags} />
          </Bloco>

          <Bloco title="Informações" description="Campos desta captação.">
            <CustomFieldsForm
              path={`/leads/${lead.id}`}
              fields={lead.customFields}
              invalidateKeys={[["lead", lead.id]]}
            />
          </Bloco>

          <Bloco title="Pontuação e resposta">
            <div className="flex items-start gap-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Pontuação
                </p>
                <p className="kpi mt-1 text-2xl font-semibold leading-none">{lead.leadScore}</p>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Primeira resposta
                </p>
                {lead.firstResponseAt ? (
                  <p className="mt-1 text-sm">
                    {relativeTime(lead.firstResponseAt)}
                    <span className="text-muted-foreground">
                      {" "}
                      · {formatSeconds(lead.businessResponseTimeSeconds)} em horário comercial
                    </span>
                  </p>
                ) : (
                  <p className="mt-1 text-sm font-medium text-warning-foreground dark:text-warning">
                    pendente
                  </p>
                )}
              </div>
            </div>
            <dl className="mt-4 space-y-2.5 border-t pt-4">
              <Item label="Entrou em" value={formatDateTime(lead.createdAt)} />
              <Item
                label="Responsável"
                value={
                  lead.owner ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Avatar name={lead.owner.fullName} size="xs" />
                      {lead.owner.fullName ?? "Sem nome"}
                      {!lead.owner.active && (
                        <span className="text-xs text-muted-foreground">(inativo)</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-warning-foreground dark:text-warning">
                      sem responsável
                    </span>
                  )
                }
              />
            </dl>
          </Bloco>

          <Bloco
            title="Dados do contato"
            description="Ficam na pessoa, não nesta captação — seguem para os próximos leads dela."
          >
            <dl className="space-y-2.5">
              <Item label="Telefone" value={formatPhone(telefone)} mono />
              <Item label="E-mail" value={lead.contact.email} />
              <Item
                label="Consentimento"
                value={
                  lead.contact.consentAt ? formatDateTime(lead.contact.consentAt) : "não registrado"
                }
              />
            </dl>
            <div className="mt-4 border-t pt-4">
              <CustomFieldsForm
                path={`/contacts/${lead.contact.id}`}
                invalidateKeys={[["lead", lead.id]]}
              />
            </div>
          </Bloco>

          <Bloco title="Atribuição" description="De onde esta captação veio.">
            <dl className="space-y-2.5">
              <Item label="Origem" value={sourceLabel(lead.source)} />
              <Item label="Plataforma" value={lead.attribution?.platform} />
              <Item label="Campanha" value={lead.attribution?.campaignName} />
              <Item label="Conjunto" value={lead.attribution?.adsetName} />
              <Item label="Anúncio" value={lead.attribution?.adName} />
            </dl>

            <div className="mt-4 border-t pt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Primeira origem
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                De onde esta <strong>pessoa</strong> veio na primeira vez. Não muda nunca, mesmo que
                ela volte por outra campanha.
              </p>
              <dl className="mt-3 space-y-2.5">
                <Item label="Plataforma" value={lead.firstTouch?.platform} />
                <Item label="Campanha" value={lead.firstTouch?.campaignName} />
                <Item label="Quando" value={formatDateTime(lead.firstTouch?.occurredAt)} />
              </dl>
            </div>

            {/* Identificadores de clique: quem precisa deles é quem audita a
                conversão, e a lista longa em fonte mono escondida atrás de um
                clique não atrapalha quem atende. */}
            <details className="group mt-4 border-t pt-3">
              <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
                Identificadores de rastreamento
              </summary>
              <dl className="mt-3 space-y-2">
                <Mono label="ctwa_clid" value={lead.attribution?.ctwaClid} />
                <Mono label="fbclid" value={lead.attribution?.fbclid} />
                <Mono label="fbc" value={lead.attribution?.fbc} />
                <Mono label="fbp" value={lead.attribution?.fbp} />
                <Mono label="gclid" value={lead.attribution?.gclid} />
                <Mono label="campaign_id" value={lead.attribution?.campaignExternalId} />
                <Mono label="ad_id" value={lead.attribution?.adExternalId} />
                <Mono label="utm_source" value={lead.attribution?.utmSource} />
                <Mono label="utm_campaign" value={lead.attribution?.utmCampaign} />
                <Mono label="utm_content" value={lead.attribution?.utmContent} />
              </dl>
            </details>
          </Bloco>

          <Bloco title="Jornada" description="Os toques desta pessoa antes de chegar aqui.">
            {lead.journey.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <RouteIcon className="size-4" />
                Nenhum toque registrado.
              </p>
            ) : (
              <ol className="space-y-3">
                {lead.journey.map((tp) => (
                  <li key={tp.id} className="flex gap-3 text-sm">
                    <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold tabular-nums text-muted-foreground">
                      {tp.position}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-medium">
                        {tp.campaignName ?? sourceLabel(tp.source)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {tp.channel ? `${tp.channel} · ` : ""}
                        {formatDateTime(tp.occurredAt)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Bloco>

          {answers.resumo.length > 0 && (
            <Bloco title="Resumo do formulário" description="O que as respostas indicam.">
              <dl className="space-y-2.5">
                {answers.resumo.map(([label, value], i) => (
                  <Item key={`r${i}`} label={label} value={value} />
                ))}
              </dl>
            </Bloco>
          )}

          {answers.respostas.length > 0 && (
            <Bloco title="Respostas do formulário" description="Na ordem do formulário, como a pessoa viu.">
              <ol className="space-y-3">
                {answers.respostas.map(([pergunta, resposta], i) => (
                  <li key={`p${i}`} className="text-sm">
                    <p className="text-xs text-muted-foreground">
                      {i + 1}. {pergunta}
                    </p>
                    <p className="mt-0.5 font-medium text-foreground">{resposta}</p>
                  </li>
                ))}
              </ol>
            </Bloco>
          )}
        </aside>
      </div>
    </PageBody>
  );
}

/**
 * Campos que vêm de quem enviou o lead, mas não são resposta de ninguém: ids, rastreio e controle. Ficam no
 * registro bruto e fora da ficha.
 */
const CAMPOS_TECNICOS = new Set([
  "eventId",
  "type",
  "account",
  "offer",
  "createdBy",
  "meta_event_id",
  "external_id",
  "ficha",
  "landing",
  "referrer",
  "pagina",
  "versao_consentimento",
]);

/** Nomes legíveis dos campos mais comuns; o resto vira "Campo em texto" a partir do nome técnico. */
const ROTULOS: Record<string, string> = {
  situacao: "Situação hoje",
  incomodo: "O que mais incomoda",
  dia_a_dia: "Onde aparece no dia a dia",
  se_ficar: "Se deixar para depois",
  ganho: "O que mudaria",
  ate_onde: "Até onde quer ir",
  quando: "Quando quer começar",
  onde: "Onde e em que turno",
  perfil: "Perfil",
  temperatura: "Temperatura",
  pontuacao: "Pontuação",
  prioridade: "Prioridade",
  direcao: "Direção provisória",
  unidade: "Unidade",
  ref: "Referência da mensagem",
  etiquetas: "Etiquetas",
};

function rotulo(chave: string): string {
  if (ROTULOS[chave]) return ROTULOS[chave];
  const texto = chave.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim().toLowerCase();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function textoDoValor(v: unknown): string | null {
  if (typeof v === "string") return v.trim() || null;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    const partes = v.filter((x) => typeof x === "string" || typeof x === "number").map(String);
    return partes.length > 0 ? partes.join(", ") : null;
  }
  return null;
}

/** Ordem de exibição dos campos conhecidos quando não há lista ordenada (o banco guarda objeto JSON sem ordem). */
const ORDEM = Object.keys(ROTULOS);

/** Lista de pares vinda do formulário externo: `[{pergunta, resposta}]` ou `[{rotulo, valor}]`, já na ordem. */
function pares(v: unknown, k1: string, k2: string): [string, string][] {
  if (!Array.isArray(v)) return [];
  const out: [string, string][] = [];
  for (const item of v) {
    if (!item || typeof item !== "object") continue;
    const a = textoDoValor((item as Record<string, unknown>)[k1]);
    const b = textoDoValor((item as Record<string, unknown>)[k2]);
    if (a && b) out.push([a, b]);
  }
  return out;
}

/**
 * Respostas do formulário, de onde quer que tenham vindo.
 * - Formulário externo (API de eventos, `lead.captured`): o envelope traz em `data.respostas` a pergunta e a resposta
 *   como a pessoa viu, na ordem do formulário, e em `data.resumo` o resumo em palavras. É o que a ficha mostra.
 * - Sem essas listas (formulário do próprio CRM ou envio antigo): os campos soltos, na ordem conhecida, com rótulo
 *   legível e sem os técnicos.
 */
function respostasDoFormulario(raw: Record<string, unknown> | null | undefined): {
  respostas: [string, string][];
  resumo: [string, string][];
} {
  if (!raw) return { respostas: [], resumo: [] };
  const externo =
    raw.data && typeof raw.data === "object" && !Array.isArray(raw.data) ? (raw.data as Record<string, unknown>) : {};
  const respostas = pares(externo.respostas, "pergunta", "resposta");
  const resumo = pares(externo.resumo, "rotulo", "valor");
  if (respostas.length > 0) return { respostas, resumo };

  const soltos: [string, string, number][] = [];
  for (const fonte of [raw, externo]) {
    for (const [chave, valor] of Object.entries(fonte)) {
      if (chave === "data" || chave === "respostas" || chave === "resumo" || CAMPOS_TECNICOS.has(chave)) continue;
      const texto = textoDoValor(valor);
      const pos = ORDEM.indexOf(chave);
      if (texto) soltos.push([rotulo(chave), texto, pos < 0 ? ORDEM.length + soltos.length : pos]);
    }
  }
  soltos.sort((a, b) => a[2] - b[2]);
  return { respostas: soltos.map(([r, t]) => [r, t] as [string, string]), resumo };
}

/** Cartão da lateral: título, uma linha de contexto opcional e o conteúdo. */
function Bloco({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-col gap-0">
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Item({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactNode | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("break-words text-sm", mono && "tabular-nums")}>{value || "—"}</dd>
    </div>
  );
}

/** Identificador técnico: fonte monoespaçada e quebra de linha por caractere. */
function Mono({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="break-all font-mono text-xs">{value ?? "—"}</dd>
    </div>
  );
}

function FichaCarregando() {
  return (
    <PageBody className="space-y-5" aria-busy>
      <Skeleton className="h-4 w-14" />
      <Skeleton className="h-56 rounded-xl sm:h-48" />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-6">
        <Skeleton className="h-96 rounded-xl" />
        <div className="space-y-5">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    </PageBody>
  );
}

function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
}
